import { uploadFile } from '@/lib/upload';
import { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import {
  ArrowLeft,
  Lock,
  MessageCircle,
  Reply,
  Send,
  Paperclip,
  X,
  Pencil,
  Trash2,
  Search,
  SlidersHorizontal,
  ChevronDown,
  Ellipsis,
  AtSign,
  Type,
  FolderOpen,
  Check,
  Users,
  ClipboardList,
  ArrowUpRight,
} from 'lucide-react';
import { richTextToPlain, type InboxThreadDto, type InboxMessageDto, type RichText } from '@cw/shared';
import { api } from '@/lib/api';
import { useCan, useMe } from '@/lib/session';
import { handleFormError } from '@/lib/forms';
import { useClients } from '@/lib/clients';
import { useProjects } from '@/lib/projects';
import { Button } from '@/design-system/components/button';
import { Input } from '@/design-system/components/input';
import { Select } from '@/design-system/components/select';
import { Segmented } from '@/design-system/components/segmented';
import { Skeleton } from '@/design-system/components/skeleton';
import { EmptyState, ErrorState } from '@/design-system/components/states';
import { RichView } from '@/design-system/components/rich-view';
import { Dialog, DialogContent } from '@/design-system/components/dialog';
import { RichEditor } from '@/design-system/components/rich-text';
import { cn } from '@/lib/utils';
import { Avatar } from '@/design-system/components/avatar';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '@/design-system/components/menu';
import { Tooltip } from '@/design-system/components/tooltip';
import { useLayoutEffect } from 'react';

type Context = { type: 'client' | 'task' | 'request' | 'project'; id: string };
export function ContextInbox({ type, id, fill = false }: Context & { fill?: boolean }) {
  const internal = useCan('comments:internal');
  const [channel, setChannel] = useState<'client' | 'internal'>(internal ? 'internal' : 'client');
  const q = useQuery({
    queryKey: ['inbox', 'context', type, id, channel],
    queryFn: () => api.post<InboxThreadDto>('/inbox/context', { type, id, channel }),
  });
  return (
    <div className={cn('space-y-4', fill && 'flex min-h-0 flex-1 flex-col')}>
      {internal && (
        <Segmented
          label="Visibilidade da conversa"
          value={channel}
          onChange={setChannel}
          options={[
            { value: 'client', label: 'Com o cliente' },
            { value: 'internal', label: 'Equipe interna' },
          ]}
        />
      )}
      {q.isPending ? (
        <Skeleton className="h-48" />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <div
          className={cn(
            fill && 'flex min-h-0 flex-1 overflow-hidden rounded-panel border border-line bg-stage',
          )}
        >
          <ThreadView key={q.data.id} thread={q.data} fill={fill} />
        </div>
      )}
    </div>
  );
}
export function InboxLink({ type, id, label = 'Abrir Inbox' }: Context & { label?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        <MessageCircle />
        {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          title="Inbox"
          className="flex h-[min(720px,calc(100dvh-32px))] max-w-3xl flex-col overflow-hidden"
        >
          <ContextInbox type={type} id={id} fill />
        </DialogContent>
      </Dialog>
    </>
  );
}
export function useInboxThreads(clientId?: string) {
  return useQuery({
    queryKey: ['inbox', 'threads', clientId ?? 'all'],
    queryFn: () => api.get<InboxThreadDto[]>(`/inbox/threads${clientId ? `?clientId=${clientId}` : ''}`),
    refetchInterval: 15000,
  });
}
function conversationDate(value: string, short = false) {
  const date = new Date(value);
  const today = new Date();
  if (date.toDateString() === today.toDateString())
    return short ? date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : 'Hoje';
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Ontem';
  return date.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: short ? 'short' : 'long',
    ...(date.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}),
  });
}
const contextLabel = (t: InboxThreadDto) =>
  t.contextType === 'client'
    ? 'Conversa geral'
    : t.contextType === 'task'
      ? 'Tarefa'
      : t.contextType === 'project'
        ? 'Projeto'
        : 'Pedido';

export function ConversationsPage({ clientId: lockedClient }: { clientId?: string } = {}) {
  const me = useMe();
  const portal = me.role.startsWith('client_');
  const [params, setParams] = useSearchParams();
  const selected = params.get('thread');
  const [client, setClient] = useState('all');
  const [project, setProject] = useState('all');
  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const clientId = lockedClient ?? (client === 'all' ? undefined : client);
  const clients = useClients();
  const projects = useProjects(clientId ? { clientId } : {});
  const qs = new URLSearchParams({
    ...(clientId ? { clientId } : {}),
    ...(project !== 'all' ? { projectId: project } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(onlyUnread ? { unread: '1' } : {}),
  });
  const list = useQuery({
    queryKey: ['inbox', 'threads', qs.toString()],
    queryFn: () => api.get<InboxThreadDto[]>(`/inbox/threads?${qs}`),
    refetchInterval: 15000,
  });
  const detail = useQuery({
    queryKey: ['inbox', 'thread', selected],
    queryFn: () => api.get<InboxThreadDto>(`/inbox/threads/${selected}`),
    enabled: Boolean(selected),
  });
  const choose = (id?: string) =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      if (id) next.set('thread', id);
      else next.delete('thread');
      return next;
    });
  const start = useMutation({
    mutationFn: () =>
      api.post<InboxThreadDto>('/inbox/context', {
        type: 'client',
        id: clientId ?? clients.data?.[0]?.id,
        channel: 'client',
      }),
    onSuccess: (t) => {
      choose(t.id);
      void list.refetch();
    },
    onError: (e) => handleFormError(e),
  });
  const unread = (list.data ?? []).filter((t) => t.unread > 0).length;
  const active = detail.data;
  const contextHref =
    active?.contextType === 'task'
      ? portal
        ? active.projectId
          ? `/portal/projects/${active.projectId}`
          : undefined
        : `?thread=${active.id}&task=${active.contextId}`
      : active?.contextType === 'request'
        ? portal
          ? '/portal/requests'
          : `/requests?r=${active.contextId}`
        : active?.contextType === 'project'
          ? portal
            ? `/portal/projects/${active.contextId}`
            : `/projects/p/${active.contextId}`
          : undefined;
  return (
    <section
      className={cn(
        'flex min-h-[520px] min-w-0 flex-col gap-4',
        lockedClient
          ? 'h-[calc(100dvh-290px)]'
          : portal
            ? 'h-[calc(100dvh-140px)] max-lg:h-[calc(100dvh-180px)]'
            : 'h-[calc(100dvh-80px)] px-4 py-4 md:px-6',
        'max-md:min-h-[460px]',
      )}
      aria-label="Inbox de conversas"
    >
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex size-10 items-center justify-center rounded-[14px] bg-signal/10 text-signal">
            <MessageCircle className="size-5" />
          </span>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Inbox</h1>
            <p className="text-xs text-fg-muted">Uma conversa para cada trabalho.</p>
          </div>
        </div>
        {!portal && !lockedClient && (
          <Button asChild size="sm" variant="ghost">
            <Link to="/requests">
              <ClipboardList /> Triagem de pedidos
            </Link>
          </Button>
        )}
      </header>
      <div className="grid min-h-0 flex-1 overflow-hidden rounded-[20px] border border-line bg-canvas/30 shadow-[0_8px_32px_-20px_rgb(0_0_0/0.5)] md:grid-cols-[290px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)]">
        <aside
          aria-label="Lista de conversas"
          className={cn(
            'flex min-h-0 flex-col border-line bg-raised/30 md:border-r',
            selected && 'hidden md:flex',
          )}
        >
          <div className="shrink-0 border-b border-line p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">
                Conversas{' '}
                {unread > 0 && (
                  <span className="ml-1 rounded-full bg-signal/15 px-1.5 py-0.5 text-[11px] text-signal">
                    {unread}
                  </span>
                )}
              </h2>
              <div className="flex gap-1">
                <Tooltip label="Filtros">
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label="Filtrar conversas"
                    aria-expanded={filtersOpen}
                    onClick={() => setFiltersOpen(!filtersOpen)}
                  >
                    <SlidersHorizontal />
                  </Button>
                </Tooltip>
                <Tooltip label="Abrir conversa geral do cliente selecionado">
                  <Button
                    size="icon-sm"
                    aria-label="Conversa geral"
                    disabled={!clientId && (clients.data?.length ?? 0) !== 1}
                    loading={start.isPending}
                    onClick={() => start.mutate()}
                  >
                    <MessageCircle />
                  </Button>
                </Tooltip>
              </div>
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute top-2.5 left-3 size-4 text-fg-subtle" />
              <Input
                aria-label="Buscar conversas e mensagens"
                placeholder="Buscar uma conversa…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 rounded-full pl-9 text-xs"
              />
            </div>
            <div className="mt-3 flex gap-1" aria-label="Filtrar por leitura">
              {[
                { value: false, label: 'Todas' },
                { value: true, label: 'Não lidas' },
              ].map((o) => (
                <button
                  key={o.label}
                  type="button"
                  aria-pressed={onlyUnread === o.value}
                  onClick={() => setOnlyUnread(o.value)}
                  className={cn(
                    'rounded-full px-3 py-1.5 text-xs transition-colors focus-visible:outline-2 focus-visible:outline-signal',
                    onlyUnread === o.value ? 'bg-press font-medium text-fg' : 'text-fg-subtle hover:text-fg',
                  )}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {filtersOpen && (
              <div className="mt-3 space-y-2 border-t border-line pt-3">
                {!lockedClient && !portal && (
                  <Select
                    aria-label="Cliente"
                    value={client}
                    onValueChange={(v) => {
                      setClient(v);
                      setProject('all');
                      choose();
                    }}
                    className="h-9 text-xs"
                    options={[
                      { value: 'all', label: 'Todos os clientes' },
                      ...(clients.data ?? []).map((c) => ({ value: c.id, label: c.name })),
                    ]}
                  />
                )}
                <Select
                  aria-label="Projeto"
                  value={project}
                  onValueChange={setProject}
                  className="h-9 text-xs"
                  options={[
                    { value: 'all', label: 'Todos os projetos' },
                    ...(projects.data ?? []).map((p) => ({ value: p.id, label: p.name })),
                  ]}
                />
              </div>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2">
            {list.isPending ? (
              <div className="space-y-2 p-2">
                {[0, 1, 2].map((n) => (
                  <Skeleton key={n} className="h-20 rounded-control" />
                ))}
              </div>
            ) : list.isError ? (
              <ErrorState error={list.error} onRetry={() => list.refetch()} />
            ) : !list.data.length ? (
              <EmptyState
                className="px-3 py-10"
                icon={<MessageCircle />}
                title={search || onlyUnread ? 'Nenhuma conversa encontrada' : 'Vamos conversar?'}
                description={
                  search || onlyUnread
                    ? 'Tente outra busca ou altere os filtros.'
                    : 'Escolha um cliente no filtro para abrir a conversa geral. Conversas de tarefas também aparecem aqui.'
                }
              />
            ) : (
              <ul className="space-y-1">
                {list.data.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      aria-current={selected === t.id ? 'true' : undefined}
                      onClick={() => choose(t.id)}
                      className={cn(
                        'group flex w-full gap-3 rounded-[14px] p-3 text-left transition-colors focus-visible:outline-2 focus-visible:outline-signal',
                        selected === t.id ? 'bg-press' : 'hover:bg-hover',
                      )}
                    >
                      <span
                        className="relative flex size-10 shrink-0 items-center justify-center rounded-[14px]"
                        style={{
                          background: `color-mix(in srgb,var(--color-label-${t.color}) 14%,var(--color-raised))`,
                          color: `var(--color-label-${t.color})`,
                        }}
                      >
                        {t.channel === 'internal' ? (
                          <Lock className="size-4" />
                        ) : t.contextType === 'client' ? (
                          <MessageCircle className="size-5" />
                        ) : (
                          <ClipboardList className="size-4" />
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className="truncate text-[13px] font-semibold">
                            {t.contextType === 'client' ? (t.clientName ?? 'Conversa geral') : t.title}
                          </span>
                          <time dateTime={t.lastAt} className="shrink-0 text-[10px] text-fg-subtle">
                            {conversationDate(t.lastAt, true)}
                          </time>
                        </span>
                        <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-fg-subtle">
                          <span className="truncate">
                            {t.contextType === 'client' ? 'Conversa geral' : (t.clientName ?? 'Estúdio')}
                          </span>
                          {t.channel === 'internal' && (
                            <span className="shrink-0 text-label-amber">Interna</span>
                          )}
                        </span>
                        <span className="mt-1 flex items-center justify-between gap-2">
                          <span
                            className={cn('truncate text-xs', t.unread ? 'text-fg-muted' : 'text-fg-subtle')}
                          >
                            {t.preview || 'Envie a primeira mensagem'}
                          </span>
                          {t.unread > 0 && (
                            <span
                              className="flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-signal px-1 text-[10px] font-semibold text-signal-fg"
                              aria-label={`${t.unread} mensagens não lidas`}
                            >
                              {t.unread > 99 ? '99+' : t.unread}
                            </span>
                          )}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
        <div className={cn('flex min-h-0 min-w-0 flex-col bg-stage', !selected && 'hidden md:flex')}>
          {selected ? (
            detail.isPending ? (
              <Skeleton className="m-5 h-64" />
            ) : detail.isError ? (
              <ErrorState error={detail.error} onRetry={() => detail.refetch()} />
            ) : (
              active && (
                <>
                  <header className="flex shrink-0 items-center gap-3 border-b border-line bg-raised/20 px-4 py-3 md:px-5">
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label="Voltar para conversas"
                      onClick={() => choose()}
                      className="md:hidden"
                    >
                      <ArrowLeft />
                    </Button>
                    <Avatar name={active.clientName ?? active.title} size="sm" />
                    <div className="min-w-0 flex-1">
                      <h2 className="truncate text-sm font-semibold">
                        {active.contextType === 'client' ? (active.clientName ?? active.title) : active.title}
                      </h2>
                      <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-fg-subtle">
                        {active.channel === 'internal' ? (
                          <Lock className="size-3 text-label-amber" />
                        ) : (
                          <Users className="size-3" />
                        )}
                        <span className="truncate">
                          {active.channel === 'internal'
                            ? 'Equipe interna'
                            : portal
                              ? 'Você e a equipe'
                              : 'Com o cliente'}{' '}
                          · {contextLabel(active)}
                        </span>
                      </p>
                    </div>
                    {contextHref && (
                      <Tooltip label="Abrir trabalho">
                        <Button asChild size="icon-sm" variant="ghost">
                          <Link to={contextHref} aria-label="Abrir trabalho">
                            <ArrowUpRight />
                          </Link>
                        </Button>
                      </Tooltip>
                    )}
                  </header>
                  <ThreadView key={selected} thread={active} fill />
                </>
              )
            )
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center px-8 py-12 text-center">
              <div className="relative mb-6 flex size-20 items-center justify-center rounded-[28px] border border-line bg-raised">
                <MessageCircle className="size-9 text-fg-muted" />
                <span className="absolute -right-2 -bottom-2 flex size-8 items-center justify-center rounded-full bg-signal text-signal-fg">
                  <Check className="size-4" />
                </span>
              </div>
              <h2 className="text-xl font-semibold tracking-tight">Seu próximo alinhamento começa aqui</h2>
              <p className="mt-3 max-w-xs text-sm leading-relaxed text-fg-muted">
                Abra uma conversa ao lado. Briefings, arquivos e decisões ficam juntos do trabalho.
              </p>
              <span className="mt-6 flex items-center gap-2 text-xs text-fg-subtle">
                <Lock className="size-3" /> Cada cliente no seu espaço
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
export function ThreadView({ thread: t, fill = false }: { thread: InboxThreadDto; fill?: boolean }) {
  const qc = useQueryClient();
  const [formatting, setFormatting] = useState(false);
  const history = useRef<HTMLDivElement>(null);
  const follow = useRef(true);
  const metrics = useRef<{ last?: string; count: number; height: number }>({ count: 0, height: 0 });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<RichText>({ type: 'doc', content: [{ type: 'paragraph' }] });
  const [editorKey, setEditorKey] = useState(0);
  const text = richTextToPlain(draft);
  const resetDraft = (doc: RichText = { type: 'doc', content: [{ type: 'paragraph' }] }) => {
    setDraft(doc);
    setEditorKey((v) => v + 1);
  };
  const [reply, setReply] = useState<InboxMessageDto | null>(null);
  const [editing, setEditing] = useState<InboxMessageDto | null>(null);
  const [fileIds, setFileIds] = useState<string[]>([]);
  const [mentions, setMentions] = useState<string[]>([]);
  const [attach, setAttach] = useState(false);
  const query = useInfiniteQuery({
    queryKey: ['inbox', 'messages', t.id],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.get<{ messages: InboxMessageDto[]; next: string | null }>(
        `/inbox/threads/${t.id}/messages${pageParam ? `?before=${pageParam}` : ''}`,
      ),
    getNextPageParam: (p) => p.next ?? undefined,
    refetchInterval: 10000,
  });
  const people = useQuery({
    queryKey: ['inbox', 'people', t.id],
    queryFn: () => api.get<{ id: string; name: string }[]>(`/inbox/threads/${t.id}/people`),
  });
  const files = useQuery({
    queryKey: ['inbox', 'files', t.id],
    queryFn: () => api.get<{ id: string; name: string; url: string }[]>(`/inbox/threads/${t.id}/files`),
    enabled: attach,
  });
  const messages = [...(query.data?.pages ?? [])].reverse().flatMap((p) => p.messages);
  const last = messages.at(-1)?.id;
  const refresh = () => qc.invalidateQueries({ queryKey: ['inbox'] });
  useEffect(() => {
    if (!last) return;
    const mark = () => {
      if (document.visibilityState === 'visible')
        void api
          .post(`/inbox/threads/${t.id}/read`, { through: last })
          .then(() => qc.invalidateQueries({ queryKey: ['inbox', 'threads'] }))
          .catch(() => undefined);
    };
    mark();
    document.addEventListener('visibilitychange', mark);
    return () => document.removeEventListener('visibilitychange', mark);
  }, [last, t.id, qc]);
  const send = useMutation({
    mutationFn: () =>
      editing
        ? api.patch(`/inbox/threads/${t.id}/messages/${editing.id}`, { body: draft })
        : api.post(`/inbox/threads/${t.id}/messages`, {
            body: draft,
            fileIds,
            mentions,
            ...(reply ? { replyToId: reply.id } : {}),
          }),
    onSuccess: () => {
      resetDraft();
      setReply(null);
      setEditing(null);
      setFileIds([]);
      setMentions([]);
      void refresh();
      follow.current = true;
    },
    onError: (e) => handleFormError(e),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/inbox/threads/${t.id}/messages/${id}`),
    onSuccess: refresh,
    onError: (e) => handleFormError(e),
  });
  const react = (id: string, emoji: string) =>
    api
      .post(`/inbox/threads/${t.id}/messages/${id}/reaction`, { emoji })
      .then(refresh)
      .catch(handleFormError);
  useLayoutEffect(() => {
    const el = history.current;
    if (!el) return;
    const previous = metrics.current;
    if (!previous.last || (previous.last !== last && follow.current)) el.scrollTop = el.scrollHeight;
    else if (previous.last === last && messages.length > previous.count)
      el.scrollTop += el.scrollHeight - previous.height;
    metrics.current = { last, count: messages.length, height: el.scrollHeight };
  }, [last, messages.length]);
  const canSend =
    Boolean(text.trim() || fileIds.length) &&
    !query.isError &&
    !query.isPending &&
    !uploading &&
    !send.isPending;
  return (
    <div
      className={cn(
        'flex min-h-0 min-w-0 flex-col',
        fill
          ? 'flex-1'
          : 'h-[min(580px,70dvh)] min-h-[360px] overflow-hidden rounded-panel border border-line bg-stage',
      )}
    >
      <div
        ref={history}
        role="log"
        onScroll={(e) => {
          const el = e.currentTarget;
          follow.current = el.scrollHeight - el.scrollTop - el.clientHeight < 96;
        }}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 md:px-6"
        aria-label="Histórico da conversa"
      >
        {query.hasNextPage && (
          <div className="mb-5 text-center">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => query.fetchNextPage()}
              loading={query.isFetchingNextPage}
            >
              <ChevronDown className="rotate-180" /> Mensagens anteriores
            </Button>
          </div>
        )}
        {query.isPending ? (
          <div className="space-y-4">
            <Skeleton className="h-16 w-2/3 rounded-[18px]" />
            <Skeleton className="ml-auto h-20 w-1/2 rounded-[18px]" />
          </div>
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : !messages.length ? (
          <div className="flex h-full min-h-36 flex-col items-center justify-center text-center">
            <MessageCircle className="mb-3 size-8 text-fg-subtle" />
            <p className="text-sm font-medium">Comece com uma mensagem</p>
            <p className="mt-1 max-w-xs text-xs leading-relaxed text-fg-subtle">
              Um alinhamento rápido, uma referência ou o próximo passo do trabalho.
            </p>
          </div>
        ) : (
          <ol className="space-y-3">
            {messages.map((m, index) => {
              const previous = messages[index - 1];
              const dayChanged =
                !previous ||
                new Date(previous.createdAt).toDateString() !== new Date(m.createdAt).toDateString();
              return (
                <li key={m.id} id={`message-${m.id}`}>
                  {dayChanged && (
                    <div className="my-6 flex items-center justify-center gap-4">
                      <span className="h-px max-w-12 flex-1 bg-line" />
                      <span className="text-[10px] font-medium text-fg-subtle">
                        {conversationDate(m.createdAt)}
                      </span>
                      <span className="h-px max-w-12 flex-1 bg-line" />
                    </div>
                  )}
                  <div className={cn('group flex items-end gap-2', m.mine && 'flex-row-reverse')}>
                    {!m.mine && (
                      <Avatar name={m.authorName} size="xs" className="mb-1 hidden sm:inline-flex" />
                    )}
                    <div
                      className={cn(
                        'relative min-w-0 max-w-[90%] rounded-[18px] px-3.5 py-2.5 text-sm sm:max-w-[78%]',
                        m.mine
                          ? 'rounded-br-[5px] bg-signal/10 shadow-[inset_0_0_0_1px_rgb(212_255_58/0.10)]'
                          : 'rounded-bl-[5px] bg-raised',
                      )}
                    >
                      {!m.mine && (
                        <p className="mb-1.5 text-[11px] font-semibold text-fg-muted">{m.authorName}</p>
                      )}
                      {m.reply && (
                        <blockquote className="mb-2 max-w-full rounded-[8px] border-l-2 border-signal bg-canvas/30 px-2.5 py-2 text-xs">
                          <p className="mb-0.5 font-medium text-fg-muted">{m.reply.authorName}</p>
                          <p className="line-clamp-2 break-words text-fg-subtle">{m.reply.text}</p>
                        </blockquote>
                      )}
                      {m.deleted ? (
                        <p className="text-xs italic text-fg-subtle">Mensagem apagada</p>
                      ) : (
                        <>
                          <RichView
                            doc={m.body}
                            className="break-words text-[13.5px] leading-relaxed [&_p]:my-0.5 [overflow-wrap:anywhere]"
                          />
                          {m.files.map((f) => (
                            <a
                              key={f.id}
                              href={f.url}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-2 flex max-w-full items-center gap-2 rounded-[10px] border border-line bg-canvas/30 px-3 py-2.5 text-xs hover:bg-hover"
                            >
                              <span className="flex size-8 shrink-0 items-center justify-center rounded-[8px] bg-raised">
                                <Paperclip className="size-4" />
                              </span>
                              <span className="min-w-0 flex-1 break-all">{f.name}</span>
                              <ArrowUpRight className="size-3 shrink-0 text-fg-subtle" />
                            </a>
                          ))}
                        </>
                      )}
                      <div className="mt-1.5 flex items-center justify-end gap-1.5">
                        <time dateTime={m.createdAt} className="text-[10px] text-fg-subtle">
                          {m.editedAt && 'editada · '}
                          {new Date(m.createdAt).toLocaleTimeString('pt-BR', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </time>
                        {m.mine && !m.deleted && (
                          <Check className="size-3 text-fg-subtle" aria-label="Enviada" />
                        )}
                      </div>
                      {!!m.reactions.length && !m.deleted && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {m.reactions.map((r) => (
                            <button
                              key={r.emoji}
                              type="button"
                              aria-label={`Reação ${r.emoji}: ${r.count}`}
                              aria-pressed={r.mine}
                              onClick={() => void react(m.id, r.emoji)}
                              className={cn(
                                'rounded-full border px-2 py-0.5 text-[11px] transition-colors focus-visible:outline-2 focus-visible:outline-signal',
                                r.mine
                                  ? 'border-signal/40 bg-signal/10'
                                  : 'border-line bg-canvas/40 hover:bg-hover',
                              )}
                            >
                              {r.emoji} {r.count}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {!m.deleted && (
                      <Menu>
                        <MenuTrigger asChild>
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            aria-label={`Ações da mensagem de ${m.authorName}`}
                            className="mb-1 shrink-0 text-fg-subtle opacity-60 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 focus:opacity-100"
                          >
                            <Ellipsis />
                          </Button>
                        </MenuTrigger>
                        <MenuContent align={m.mine ? 'end' : 'start'}>
                          <MenuItem
                            icon={<Reply />}
                            onSelect={() => {
                              setReply(m);
                              setEditing(null);
                            }}
                          >
                            Responder mensagem
                          </MenuItem>
                          <MenuItem onSelect={() => void react(m.id, '👍')}>👍 Reagir com gostei</MenuItem>
                          {m.mine && (
                            <>
                              <MenuItem
                                icon={<Pencil />}
                                onSelect={() => {
                                  setEditing(m);
                                  setReply(null);
                                  resetDraft(m.body);
                                }}
                              >
                                Editar mensagem
                              </MenuItem>
                              <MenuItem icon={<Trash2 />} danger onSelect={() => remove.mutate(m.id)}>
                                Apagar mensagem
                              </MenuItem>
                            </>
                          )}
                        </MenuContent>
                      </Menu>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
      <div className="shrink-0 border-t border-line bg-stage px-3 pt-3 pb-2 md:px-5">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSend) send.mutate();
          }}
          className="rounded-[16px] border border-line-strong bg-canvas/40 p-2 focus-within:border-fg-subtle/50 transition-colors"
        >
          {(reply || editing) && (
            <div className="mb-1 flex items-center gap-2 rounded-[10px] border-l-2 border-signal bg-raised px-3 py-2">
              <Reply className="size-3.5 shrink-0 text-fg-subtle" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium">
                  {editing ? 'Editando mensagem' : `Respondendo a ${reply?.authorName}`}
                </p>
                {reply && (
                  <p className="mt-0.5 truncate text-xs text-fg-subtle">{richTextToPlain(reply.body)}</p>
                )}
              </div>
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                aria-label="Cancelar resposta ou edição"
                onClick={() => {
                  setReply(null);
                  setEditing(null);
                  resetDraft();
                }}
              >
                <X />
              </Button>
            </div>
          )}
          {!!fileIds.length && (
            <div className="mb-1 flex max-h-24 flex-wrap gap-1.5 overflow-y-auto px-2 py-1">
              {fileIds.map((id) => (
                <span
                  key={id}
                  className="flex max-w-full items-center gap-1.5 rounded-[8px] border border-line bg-raised py-1 pr-1 pl-2 text-[11px]"
                >
                  <Paperclip className="size-3 shrink-0" />
                  <span className="max-w-40 truncate">
                    {files.data?.find((f) => f.id === id)?.name ?? 'Arquivo anexado'}
                  </span>
                  <button
                    type="button"
                    aria-label="Remover anexo da mensagem"
                    onClick={() => setFileIds((ids) => ids.filter((v) => v !== id))}
                    className="rounded p-1 hover:bg-hover focus-visible:outline-2 focus-visible:outline-signal"
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <RichEditor
            key={editorKey}
            ariaLabel="Mensagem"
            autoFocus={editorKey > 0}
            value={draft}
            onChange={setDraft}
            minHeight={38}
            compact
            showToolbar={formatting}
            onSubmit={() => {
              if (canSend) send.mutate();
            }}
            placeholder={t.channel === 'internal' ? 'Escreva para a equipe…' : 'Escreva uma mensagem…'}
          />
          <div className="mt-1 flex items-center gap-0.5">
            <label
              className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-[9px] text-fg-subtle transition-colors hover:bg-hover hover:text-fg focus-within:outline-2 focus-within:outline-signal"
              title="Enviar arquivo"
            >
              <Paperclip className="size-4" />
              <input
                aria-label="Enviar arquivo"
                type="file"
                multiple
                disabled={uploading || send.isPending}
                className="sr-only"
                onChange={async (e) => {
                  const selectedFiles = Array.from(e.target.files ?? []);
                  e.target.value = '';
                  setUploading(true);
                  setUploadError(null);
                  try {
                    for (const file of selectedFiles) {
                      const result = await uploadFile(
                        file,
                        t.contextType === 'task'
                          ? { purpose: 'task_attachment', taskId: t.contextId, visibility: t.channel }
                          : t.contextType === 'request'
                            ? { purpose: 'request_attachment', requestId: t.contextId, visibility: t.channel }
                            : t.contextType === 'project'
                              ? { purpose: 'project_file', projectId: t.contextId, visibility: t.channel }
                              : { purpose: 'client_file', clientId: t.contextId, visibility: t.channel },
                      );
                      setFileIds((v) => [...v, result.id]);
                    }
                    setAttach(true);
                    void qc.invalidateQueries({ queryKey: ['inbox', 'files', t.id] });
                  } catch (e) {
                    setUploadError(e instanceof Error ? e.message : 'Não foi possível enviar o arquivo');
                  } finally {
                    setUploading(false);
                  }
                }}
              />
            </label>
            <Tooltip label="Arquivos do trabalho">
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                aria-label="Arquivos do trabalho"
                aria-expanded={attach}
                onClick={() => setAttach(!attach)}
              >
                <FolderOpen />
              </Button>
            </Tooltip>
            <Menu>
              <MenuTrigger asChild>
                <Button type="button" size="icon-sm" variant="ghost" aria-label="Mencionar participante">
                  <AtSign />
                </Button>
              </MenuTrigger>
              <MenuContent className="max-h-60 overflow-y-auto">
                {people.isPending ? (
                  <p className="p-3 text-xs text-fg-muted">Carregando pessoas…</p>
                ) : people.isError ? (
                  <p role="alert" className="p-3 text-xs text-danger">
                    Não foi possível carregar os participantes.
                  </p>
                ) : !people.data.length ? (
                  <p className="p-3 text-xs text-fg-subtle">Nenhum participante disponível.</p>
                ) : (
                  people.data.map((p) => (
                    <MenuItem
                      key={p.id}
                      onSelect={() => {
                        setMentions((v) => [...new Set([...v, p.id])]);
                        resetDraft({
                          ...draft,
                          content: [
                            ...(draft.content ?? []),
                            { type: 'paragraph', content: [{ type: 'text', text: `@${p.name} ` }] },
                          ],
                        });
                      }}
                    >
                      <Avatar name={p.name} size="xs" />
                      {p.name}
                    </MenuItem>
                  ))
                )}
              </MenuContent>
            </Menu>
            <Tooltip label="Formatação">
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                aria-label="Formatação da mensagem"
                aria-pressed={formatting}
                onClick={() => setFormatting(!formatting)}
              >
                <Type />
              </Button>
            </Tooltip>
            <span className="ml-auto mr-2 hidden text-[10px] text-fg-subtle sm:inline">Enter envia</span>
            <Button
              type="submit"
              size="sm"
              variant="primary"
              className="ml-auto rounded-[10px] sm:ml-0"
              aria-label={editing ? 'Salvar mensagem' : 'Enviar mensagem'}
              disabled={!canSend}
              loading={send.isPending || uploading}
            >
              <Send className="size-3.5" />
              <span>{editing ? 'Salvar' : 'Enviar'}</span>
            </Button>
          </div>
          {uploadError && (
            <p role="alert" className="mt-2 px-2 text-xs text-danger">
              {uploadError}
            </p>
          )}
          {attach && (
            <div className="mt-2 max-h-32 space-y-2 overflow-y-auto border-t border-line px-2 pt-2">
              {files.isPending ? (
                <Skeleton className="h-12" />
              ) : files.isError ? (
                <ErrorState error={files.error} onRetry={() => files.refetch()} />
              ) : !files.data.length ? (
                <p className="py-2 text-xs text-fg-subtle">
                  Envie um arquivo pelo clipe para anexar à conversa.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {files.data.map((f) => (
                    <Button
                      key={f.id}
                      type="button"
                      size="sm"
                      className="max-w-full [&_span]:truncate"
                      aria-pressed={fileIds.includes(f.id)}
                      variant={fileIds.includes(f.id) ? 'primary' : 'secondary'}
                      onClick={() =>
                        setFileIds((v) => (v.includes(f.id) ? v.filter((x) => x !== f.id) : [...v, f.id]))
                      }
                    >
                      <Paperclip />
                      <span>{f.name}</span>
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}
        </form>
        <p className="flex items-center justify-center gap-1.5 pt-2 text-[10px] text-fg-subtle">
          {t.channel === 'internal' ? (
            <>
              <Lock className="size-2.5" /> Somente a equipe deste trabalho
            </>
          ) : (
            <>
              <Users className="size-2.5" /> Conversa compartilhada com o cliente
            </>
          )}
          <span className="hidden sm:inline"> · Shift + Enter para nova linha</span>
        </p>
      </div>
    </div>
  );
}
